import React from 'react';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Box,
  Container,
} from '@mui/material';
import QRCode from 'qrcode.react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getFoodcourt } from 'apis/foodcourtAPIs';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.grey[50],
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  foodcourtImage: {
    width: '100%',
    maxHeight: 200,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  qrCard: {
    padding: theme.spacing(3),
    textAlign: 'center',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  restaurantName: {
    marginTop: theme.spacing(2),
    fontWeight: 600,
  },
  scanText: {
    marginTop: theme.spacing(1),
    color: theme.palette.grey[500],
    fontSize: '0.75rem',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
  errorContainer: {
    textAlign: 'center',
    paddingTop: theme.spacing(10),
  },
}));

const FoodcourtQrPage = () => {
  const classes = useStyles();
  const { foodcourtId } = useParams();

  const { data: foodcourt, isLoading, isError } = useQuery({
    queryKey: ['foodcourt', foodcourtId],
    queryFn: () => getFoodcourt(foodcourtId),
    staleTime: 5 * 60 * 1000,
  });

  const getRestaurantUrl = (restaurant) => {
    const protocol = window.location.hostname === 'localhost' ? 'http' : 'https';
    const host = window.location.port
      ? `${window.location.hostname}:${window.location.port}`
      : window.location.hostname;
    return `${protocol}://${host}/online/${encodeURI(restaurant.urlIdentifier || restaurant.id)}/dinein`;
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (isError || !foodcourt) {
    return (
      <div className={classes.errorContainer}>
        <Typography variant="h5" color="textSecondary">
          Foodcourt not found
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container maxWidth="lg">
        <div className={classes.header}>
          {foodcourt.imageUrl && (
            <img
              src={foodcourt.imageUrl}
              alt={foodcourt.name}
              className={classes.foodcourtImage}
            />
          )}
          <Typography variant="h3" gutterBottom>
            {foodcourt.name}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Scan a QR code below to order from your favourite restaurant
          </Typography>
        </div>

        <Grid container spacing={3} justifyContent="center">
          {(foodcourt.restaurants || []).map((restaurant) => (
            <Grid
              item
              xs={6}
              sm={4}
              md={3}
              key={restaurant.urlIdentifier || restaurant.id}
            >
              <Paper className={classes.qrCard} elevation={1}>
                <QRCode
                  value={getRestaurantUrl(restaurant)}
                  size={160}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="H"
                  includeMargin
                  imageSettings={{
                    src: '/static/images/orderbuddy.jpeg',
                    x: null,
                    y: null,
                    height: 20,
                    width: 20,
                    excavate: true,
                  }}
                />
                <Typography variant="subtitle1" className={classes.restaurantName}>
                  {restaurant.name}
                </Typography>
                <Typography className={classes.scanText}>Scan to order</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {(!foodcourt.restaurants || foodcourt.restaurants.length === 0) && (
          <Box textAlign="center" py={6}>
            <Typography variant="h6" color="textSecondary">
              No restaurants available yet
            </Typography>
          </Box>
        )}
      </Container>
    </div>
  );
};

export default FoodcourtQrPage;
